import Phaser from 'phaser';
import { COLORS, FONT_HEADER } from '../style.js';
import { DESIGN_WIDTH, LAYOUT } from '../PhaserGame.js';

// v0.9.9 Hourglass — toast that announces "your last turn was
// auto-skipped" when a player opens the game and discovers their
// turn expired without action. Mirrors EliminationToast's pattern
// (top-of-map banner, fade-in/hold/fade-out lifecycle, dismissable
// self-destruction).
//
// Why a toast vs a modal: the auto-skip already happened — there's
// no decision to make. A passive notification is enough; a blocking
// modal would feel punitive on top of already losing reinforcements.
// The skip + redistribution also lands in the battle log (slice 2.1)
// for the historical record.

const TOAST_WIDTH = 560;
const TOAST_HEIGHT = 72;
const TOAST_RADIUS = 8;
const TOAST_DEPTH = 500;

const FADE_IN_MS = 200;
const HOLD_MS = 3000;
const FADE_OUT_MS = 400;

export class AutoSkipToast {
  private readonly scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container | undefined;
  private tween: Phaser.Tweens.Tween | undefined;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Show the auto-skip banner. `consecutiveCount` is the number of
   * consecutive skips (1, 2, or 3). At count=3 the player was also
   * fully forfeited from the game — copy reflects that.
   */
  show(consecutiveCount: number): void {
    this.dismissImmediate();

    const cx = DESIGN_WIDTH / 2;
    const toastY = LAYOUT.topSafe + LAYOUT.hudHeight + 36;

    const isForfeit = consecutiveCount >= 3;
    const accentInt = isForfeit ? COLORS.accentRedInt : COLORS.accentAmberInt;
    const accentHex = isForfeit ? COLORS.accentRedHex : COLORS.accentAmberHex;

    const bg = this.scene.add.graphics();
    bg.fillStyle(COLORS.bgPanelInt, 0.96);
    bg.fillRoundedRect(
      -TOAST_WIDTH / 2,
      -TOAST_HEIGHT / 2,
      TOAST_WIDTH,
      TOAST_HEIGHT,
      TOAST_RADIUS
    );
    bg.lineStyle(2, accentInt, 1);
    bg.strokeRoundedRect(
      -TOAST_WIDTH / 2,
      -TOAST_HEIGHT / 2,
      TOAST_WIDTH,
      TOAST_HEIGHT,
      TOAST_RADIUS
    );

    const headline = isForfeit
      ? 'YOU FORFEITED THE GAME'
      : 'YOUR TURN WAS SKIPPED';
    const sub = isForfeit
      ? '3 turns in a row — territories redistributed.'
      : "You didn't play in time. Reinforcements forfeited.";

    const headlineText = this.scene.add
      .text(0, -12, headline, {
        fontFamily: FONT_HEADER,
        fontSize: '20px',
        color: accentHex,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5);

    const subText = this.scene.add
      .text(0, 14, sub, {
        fontFamily: FONT_HEADER,
        fontSize: '13px',
        color: COLORS.textSecondary,
      })
      .setLetterSpacing(1)
      .setOrigin(0.5);

    const container = this.scene.add.container(cx, toastY, [
      bg,
      headlineText,
      subText,
    ]);
    container.setDepth(TOAST_DEPTH);
    container.setAlpha(0);
    this.container = container;

    this.tween = this.scene.tweens.add({
      targets: container,
      alpha: 1,
      duration: FADE_IN_MS,
      onComplete: () => {
        this.scene.time.delayedCall(HOLD_MS, () => {
          if (!this.container) return;
          this.tween = this.scene.tweens.add({
            targets: this.container,
            alpha: 0,
            duration: FADE_OUT_MS,
            onComplete: () => {
              this.cleanup();
            },
          });
        });
      },
    });
  }

  private dismissImmediate(): void {
    this.tween?.stop();
    this.tween = undefined;
    this.cleanup();
  }

  private cleanup(): void {
    this.container?.destroy();
    this.container = undefined;
  }
}
